import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useHistoryCustomBack, useNavigate } from 'weblite-router'
import { useLastCallback } from '$hooks/use-last-callback'
import { getElement } from '$logic/fullscreen/helpers'
import { createCallbackManager } from '$func/callbacks'
import { FULLSCREEN_ELEMENT_CLASS } from '$setup/style/constants'

type OnExitFullscreen = () => void
export enum FullscreenLocation {
  ROOM = 'ROOM',
  NONE = 'NONE',
}

const fullscreenState: {
  enabled: boolean
  location?: FullscreenLocation
  element?: HTMLElement
  onExitFullscreen?: OnExitFullscreen
} = {
  enabled: false,
}

interface FullScreenEnabledState {
  enabled: boolean
  location?: FullscreenLocation
}
const callbackManager =
  createCallbackManager<Dispatch<SetStateAction<FullScreenEnabledState>>>()

export function useIsFullscreenEnabled(location?: FullscreenLocation) {
  const [state, setState] = useState<FullScreenEnabledState>({
    enabled: fullscreenState.enabled,
    location: fullscreenState.location,
  })

  useEffect(() => {
    callbackManager.addCallback(setState)

    return () => {
      callbackManager.removeCallback(setState)
    }
  }, [])

  return useMemo(() => {
    return state.enabled && (!location || location === state.location)
  }, [state])
}

export function useHandleFullscreenHistory(location: FullscreenLocation) {
  const navigate = useNavigate()
  const isFullscreenEnabled = useIsFullscreenEnabled(location)

  const handleBack = useLastCallback(() => {
    if (fullscreenState.element)
      exitElementFromFullscreenMode(fullscreenState.element)
    else navigate(-1)
  })

  useHistoryCustomBack(
    handleBack,
    [isFullscreenEnabled, handleBack],
    isFullscreenEnabled,
  )
}

function enableFullscreenMode(
  element: HTMLElement,
  location: FullscreenLocation,
  onExitFullscreen?: OnExitFullscreen,
) {
  fullscreenState.enabled = true
  fullscreenState.element = element
  fullscreenState.location = location
  fullscreenState.onExitFullscreen = onExitFullscreen
  callbackManager.runCallbacks({
    enabled: true,
    location,
  })
}

function disableFullscreenMode() {
  fullscreenState.enabled = false
  fullscreenState.element = undefined
  fullscreenState.location = undefined
  fullscreenState.onExitFullscreen?.()
  fullscreenState.onExitFullscreen = undefined
  callbackManager.runCallbacks({ enabled: false, location: undefined })
}

export function enterElementToFullscreenMode(
  element: string | HTMLElement,
  routerLocation: FullscreenLocation,
  onExitFullscreen?: OnExitFullscreen,
) {
  const el = getElement(element)
  if (el) {
    el.classList.add(FULLSCREEN_ELEMENT_CLASS)
    enableFullscreenMode(el, routerLocation, onExitFullscreen)
  }
}

export function exitElementFromFullscreenMode(element: string | HTMLElement) {
  const el = getElement(element)
  if (el && fullscreenState.element === el) {
    el.classList.remove(FULLSCREEN_ELEMENT_CLASS)
    disableFullscreenMode()
  }
}
